/**
 * The right-hand half of the registry: one company, in tabs.
 *
 * Overview and History are real from phase 6; Financials from phase 7B;
 * Directors from phase 8. The company website (phase 9) lives on the
 * Overview tab, per the brief's §6.5 - it is a company-level fact like the
 * registered address, not a director-level one. Contact (phase 10) is the
 * ranked candidate email addresses per director, plus the domain's mail
 * facts in plain English - built on the website above, which is why it
 * fetches lazily on its own rather than arriving with the rest of the
 * profile: unlike website/directors/financials, guesses are not generated
 * automatically the moment a company is opened.
 *
 * The rule this file exists to keep: **a SIC code is never a bare number.** The
 * API resolves every code to its catalogue description before it leaves the
 * server, and every render path here shows both.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { companiesApi } from '../lib/api.js';
import AddToListMenu from './AddToListMenu.jsx';
import FavoriteStar from './FavoriteStar.jsx';
import {
  IconAlert,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconExternal,
  IconInfo,
  IconLog,
  IconRefresh,
  IconTrash,
} from './Icons.jsx';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'financials', label: 'Financials' },
  { id: 'directors', label: 'Directors' },
  { id: 'contact', label: 'Contact' },
  { id: 'history', label: 'History' },
];

/** What each acquisition outcome meant for this company. */
const OUTCOME_META = {
  new: { label: 'First found', tone: 'badge-ok' },
  updated: { label: 'Refreshed', tone: 'badge-info' },
  skipped: { label: 'Skipped', tone: 'badge-warn' },
  error: { label: 'Error', tone: 'badge-error' },
};

/**
 * Confirmation before deleting a company - phase 11's data-protection delete
 * action (see PROJECT PLAN/DATA_PROTECTION.md). A misclick here loses
 * everything derived about the company, not just one credential, so the
 * copy names exactly what goes and what survives.
 */
function DeleteCompanyDialog({ company, onCancel, onConfirm, busy, error }) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel]);

  return (
    <div className="scrim" onClick={busy ? undefined : onCancel} role="presentation">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-company-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dialog-header">
          <div>
            <h2 className="dialog-title" id="delete-company-title">
              Delete {company.name}?
            </h2>
            <p className="dialog-description">
              Removes this company and everything derived about it — SIC codes, financials,
              directors, website and email guesses — from the registry. This cannot be undone
              from the app.{' '}
              <strong>The acquisition log keeps its record that this company was found</strong>,
              the same as any other company that was never re-acquired; re-running the SIC code
              it came from will bring it back as a fresh row, not restore what was deleted.
            </p>
          </div>
        </div>
        {error ? (
          <div className="dialog-body">
            <div className="alert alert-error">
              <span className="alert-icon">
                <IconAlert />
              </span>
              <span className="alert-body">{error}</span>
            </div>
          </div>
        ) : null}
        <div className="dialog-footer">
          <span className="dialog-footer-spacer" />
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={busy}>
            {busy && <span className="spinner" />}
            {busy ? 'Deleting' : 'Delete company'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** One labelled fact. Absent values say so rather than rendering an empty cell. */
function Fact({ label, children, tip }) {
  return (
    <div className="kv">
      <dt className="kv-key">
        {label}
        {tip ? (
          <span className="info-tip tip tip-below" data-tip={tip}>
            i
          </span>
        ) : null}
      </dt>
      <dd className="kv-value">{children ?? <span className="value-absent">Not held</span>}</dd>
    </div>
  );
}

/** A SIC code as code plus description, which is the only way it is ever shown. */
function SicCodeLine({ entry }) {
  return (
    <li className="profile-sic">
      <Link className="profile-sic-code mono" to={`/registry?sicCode=${entry.code}`}>
        {entry.code}
      </Link>
      <span className="profile-sic-description">
        {entry.description ?? (
          <span className="value-absent">
            not on the current Companies House list — a historic code this company still files
          </span>
        )}
      </span>
    </li>
  );
}

function formatDateShort(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** A yes/no/not-known flag, rendered so a genuine "no" never looks like "unknown". */
function FlagBadge({ value, yesLabel, noLabel, unknownLabel, warnOnYes }) {
  if (value === null || value === undefined) {
    return <span className="badge badge-unknown">{unknownLabel}</span>;
  }
  return (
    <span className={`badge ${value ? (warnOnYes ? 'badge-warn' : 'badge-info') : 'badge-ok'}`}>
      {value ? yesLabel : noLabel}
    </span>
  );
}

const WEBSITE_METHOD_LABELS = {
  manual: 'Entered by hand',
  domain_guess: 'Found by domain guessing',
  search: 'Found via DuckDuckGo',
};

/**
 * The website fact - phase 9. Three states: a confirmed website (with how it
 * was found), checked-and-nothing-confirmed, or never checked. All three
 * offer the same two actions: run discovery again, or paste one in by hand.
 * A hand-entered address disables discovery rather than hiding it, so the
 * tooltip can explain why rather than the button just vanishing.
 */
function WebsiteField({ company, onDiscover, discovering, onSetWebsite, settingWebsite }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const submit = (event) => {
    event.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;
    onSetWebsite(trimmed);
    setEditing(false);
  };

  return (
    <>
      {company.website ? (
        <div className="row">
          <a className="link-external" href={company.website} target="_blank" rel="noreferrer">
            {company.website}
            <IconExternal />
          </a>
          {company.websiteConfidence !== null ? (
            <span className="text-xs text-faint">
              {company.websiteConfidence}% confidence ·{' '}
              {WEBSITE_METHOD_LABELS[company.websiteSource] ?? company.websiteSource}
            </span>
          ) : null}
        </div>
      ) : company.websiteCheckedAt ? (
        <p className="value-absent text-sm">Checked - nothing could be confirmed.</p>
      ) : null}

      {editing ? (
        <form className="row" onSubmit={submit}>
          <input
            className="input"
            type="text"
            placeholder="example.co.uk"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={settingWebsite}
          />
          <button type="submit" className="btn btn-sm" disabled={settingWebsite || !draft.trim()}>
            {settingWebsite ? <span className="spinner" /> : null}
            Save
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={settingWebsite}
            onClick={() => setEditing(false)}
          >
            Cancel
          </button>
        </form>
      ) : (
        <div className="row">
          <button
            type="button"
            className="btn btn-ghost btn-sm tip tip-below tip-left"
            onClick={onDiscover}
            disabled={discovering || company.websiteSource === 'manual'}
            data-tip={
              company.websiteSource === 'manual'
                ? 'Entered by hand - discovery will never overwrite it. Enter a different address manually to change it.'
                : "Tries the company's own name as a domain against a handful of TLDs, confirmed by matching the name or number on the page, then falls back to a DuckDuckGo search. Never blocks the rest of enrichment."
            }
          >
            {discovering ? <span className="spinner" /> : <IconRefresh />}
            {company.website ? 'Re-check' : 'Discover website'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setDraft(company.website ?? '');
              setEditing(true);
            }}
          >
            Enter manually
          </button>
        </div>
      )}
    </>
  );
}

function Overview({
  company,
  onSetOverride,
  overrideSaving,
  onDiscoverWebsite,
  discoveringWebsite,
  onSetWebsite,
  settingWebsite,
}) {
  const verdict = company.agency;
  const health = company.filingHealth;

  return (
    <div className="profile-tab">
      <div className="profile-card-grid">
        <section className="profile-card">
          <h3 className="profile-section-title">Registered details</h3>
          <dl className="kv-grid">
            <Fact label="Company number">
              <a
                className="link-external mono"
                href={`https://find-and-update.company-information.service.gov.uk/company/${company.companyNumber}`}
                target="_blank"
                rel="noreferrer"
              >
                {company.companyNumber}
                <IconExternal />
              </a>
            </Fact>

            <Fact label="Company type">{company.typeLabel}</Fact>

            <Fact label="Status">
              <span className={`badge ${company.status === 'active' ? 'badge-ok' : 'badge-unknown'}`}>
                <span className="badge-dot" />
                {company.statusLabel ?? 'Unknown'}
              </span>
              {company.statusDetail ? (
                <span
                  className="badge badge-warn tip tip-below"
                  data-tip="Companies House carries the consequential half of the status here. A company can be listed as active while a proposal to strike it off is running."
                >
                  {company.statusDetail.replace(/-/g, ' ')}
                </span>
              ) : null}
            </Fact>

            <Fact label="Incorporated">{formatDate(company.incorporatedOn)}</Fact>

            {company.dissolvedOn ? (
              <Fact label="Dissolved">{formatDate(company.dissolvedOn)}</Fact>
            ) : null}

            <Fact label="Registered office">
              {company.address.length > 0 ? (
                <span className="profile-address">
                  {company.address.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </span>
              ) : null}
            </Fact>

            {company.previousNames.length > 0 ? (
              <Fact
                label="Previously named"
                tip="Every name Companies House has on file for this company, oldest changes included."
              >
                <span className="profile-address">
                  {company.previousNames.map((entry) => (
                    <span key={entry.name}>
                      {entry.name}
                      {entry.ceasedOn ? (
                        <span className="text-xs text-faint"> — until {formatDateShort(entry.ceasedOn)}</span>
                      ) : null}
                    </span>
                  ))}
                </span>
              </Fact>
            ) : null}
          </dl>
        </section>

        <section className="profile-card">
          <h3 className="profile-section-title">Classification</h3>
          <dl className="kv-grid">
            <Fact
              label="Entity type"
              tip={verdict.tip}
            >
              <span
                className={`badge ${
                  verdict.value === 'agency'
                    ? 'badge-ok'
                    : verdict.value === 'employer'
                      ? 'badge-info'
                      : 'badge-unknown'
                }`}
              >
                {verdict.label}
              </span>
              {verdict.confidence !== null ? (
                <span className="text-xs text-faint">{verdict.confidence}% confidence</span>
              ) : null}
            </Fact>

            {verdict.reason ? <Fact label="Because">{verdict.reason}</Fact> : null}

            <Fact
              label="Set by hand"
              tip="Overrides whatever the classifier decided, on this company alone. Clearing it hands the verdict back to the classifier rather than deleting it."
            >
              <div className="pill-row">
                <button
                  type="button"
                  className={`pill${verdict.source === 'override' && verdict.value === 'agency' ? ' is-active' : ''}`}
                  disabled={overrideSaving}
                  onClick={() => onSetOverride(true)}
                >
                  Mark as agency
                </button>
                <button
                  type="button"
                  className={`pill${verdict.source === 'override' && verdict.value === 'employer' ? ' is-active' : ''}`}
                  disabled={overrideSaving}
                  onClick={() => onSetOverride(false)}
                >
                  Mark as employer
                </button>
                {verdict.source === 'override' ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={overrideSaving}
                    onClick={() => onSetOverride(null)}
                  >
                    Clear override
                  </button>
                ) : null}
                {overrideSaving ? <span className="spinner" /> : null}
              </div>
            </Fact>

            <Fact
              label="Website"
              tip="Companies House publishes no website field, so this is derived: domain guessing confirmed against the page, DuckDuckGo as the fallback. An unconfirmed domain is never shown as a match."
            >
              <WebsiteField
                company={company}
                onDiscover={onDiscoverWebsite}
                discovering={discoveringWebsite}
                onSetWebsite={onSetWebsite}
                settingWebsite={settingWebsite}
              />
            </Fact>

            <Fact label="Detail held" tip={company.enrichment.tip}>
              {company.enrichment.label}
            </Fact>
          </dl>
        </section>
      </div>

      <div className="profile-card-grid">
        <section className="profile-card">
          <h3 className="profile-section-title">
            SIC codes
            <span
              className="info-tip tip tip-below"
              data-tip="What the company told Companies House it does. Up to four codes, filed in their own order — the first is conventionally the principal activity. Select one to see every other company filing it."
            >
              i
            </span>
          </h3>
          {company.sicCodes.length > 0 ? (
            <ul className="profile-sic-list">
              {company.sicCodes.map((entry) => (
                <SicCodeLine key={entry.code} entry={entry} />
              ))}
            </ul>
          ) : (
            <p className="value-absent text-sm">This company files no SIC codes.</p>
          )}
        </section>

        <section className="profile-card">
          <h3 className="profile-section-title">
            Filing &amp; compliance
            <span
              className="info-tip tip tip-below"
              data-tip="From the full Companies House profile, fetched at profile depth or above. Not held yet is a different answer from checked and clear - press Refresh above, or acquire this company's SIC code at Profiles depth or deeper."
            >
              i
            </span>
          </h3>
          {health.known ? (
            <dl className="kv-grid">
              <Fact label="Accounts due" tip="When the next set of accounts is due, and whether the last set is overdue.">
                {health.accountsNextDue ? formatDateShort(health.accountsNextDue) : null}
                {' '}
                <FlagBadge
                  value={health.accountsOverdue}
                  yesLabel="Overdue"
                  noLabel="Up to date"
                  unknownLabel="Not held"
                  warnOnYes
                />
              </Fact>
              <Fact
                label="Confirmation statement"
                tip="The annual confirmation statement, separate from the accounts filing."
              >
                {health.confirmationStatementNextDue ? formatDateShort(health.confirmationStatementNextDue) : null}
                {' '}
                <FlagBadge
                  value={health.confirmationStatementOverdue}
                  yesLabel="Overdue"
                  noLabel="Up to date"
                  unknownLabel="Not held"
                  warnOnYes
                />
              </Fact>
              <Fact label="Insolvency history" tip="Whether Companies House has ever recorded an insolvency case against this company.">
                <FlagBadge
                  value={health.hasInsolvencyHistory}
                  yesLabel="Has insolvency history"
                  noLabel="None recorded"
                  unknownLabel="Not held"
                  warnOnYes
                />
              </Fact>
              <Fact label="Charges" tip="Outstanding or satisfied charges (mortgages, debentures) registered against this company.">
                <FlagBadge
                  value={health.hasCharges}
                  yesLabel="Has registered charges"
                  noLabel="None recorded"
                  unknownLabel="Not held"
                />
              </Fact>
            </dl>
          ) : (
            <p className="value-absent text-sm">
              Not fetched yet. Press <strong>Refresh from Companies House</strong> above, or acquire this
              company&rsquo;s SIC code at Profiles depth or deeper.
            </p>
          )}
        </section>
      </div>

      {company.lastError ? (
        <div className="alert alert-warn">
          <span className="alert-icon">
            <IconAlert />
          </span>
          <span className="alert-body">
            <span className="alert-title">The last attempt to enrich this company failed</span>
            {company.lastError}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** Whole pounds, signed with a leading £ - money never renders as a bare number here. */
function formatMoney(value) {
  if (value === null || value === undefined) return null;
  const magnitude = Math.abs(Math.round(value)).toLocaleString('en-GB');
  return value < 0 ? `-£${magnitude}` : `£${magnitude}`;
}

const TREND_LABELS = {
  growing: 'Growing',
  stable: 'Stable',
  declining: 'Declining',
  insufficient_data: 'Insufficient data',
};

/** growing/declining are opposite tones on purpose - a shrinking balance sheet is worth flagging, not neutral. */
function TrendBadge({ value }) {
  if (!value || value === 'insufficient_data') {
    return <span className="badge badge-unknown">Insufficient data</span>;
  }
  const tone = value === 'growing' ? 'badge-ok' : value === 'declining' ? 'badge-warn' : 'badge-info';
  return <span className={`badge ${tone}`}>{TREND_LABELS[value] ?? value}</span>;
}

const TRADING_VERDICT_TONE = {
  actively_trading: 'badge-ok',
  minimal_activity: 'badge-warn',
  not_trading: 'badge-error',
};

function Financials({ company }) {
  const fin = company.financials;
  const periods = company.financialPeriods ?? [];
  const flags = fin.healthFlags ?? {};

  return (
    <div className="profile-tab">
      {!fin.known ? (
        <div className="empty">
          <span className="empty-icon">
            <IconInfo />
          </span>
          <h3 className="empty-title">Financial profile not fetched yet</h3>
          <p className="empty-description">
            Press <strong>Fetch accounts</strong> above, or acquire this company&rsquo;s SIC code at
            Financials depth or deeper.
          </p>
        </div>
      ) : (
        <>
          <div className="profile-card-grid">
            <section className="profile-card">
              <h3 className="profile-section-title">Trading profile</h3>
              <dl className="kv-grid">
                <Fact label="Size band" tip={fin.sizeBand.tip}>
                  {fin.sizeBand.label}
                </Fact>
                <Fact label="Trading age">
                  {fin.tradingAgeYears !== null
                    ? `${fin.tradingAgeYears} year${fin.tradingAgeYears === 1 ? '' : 's'}`
                    : null}
                </Fact>
                <Fact label="Trading verdict" tip={fin.tradingVerdict.tip}>
                  <span className={`badge ${TRADING_VERDICT_TONE[fin.tradingVerdict.value] ?? 'badge-unknown'}`}>
                    {fin.tradingVerdict.label}
                  </span>
                  {fin.tradingVerdict.reasons.length > 0 ? (
                    <ul className="profile-reasons">
                      {fin.tradingVerdict.reasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  ) : null}
                </Fact>
              </dl>
            </section>

            <section className="profile-card">
              <h3 className="profile-section-title">
                Latest filed figures
                {fin.netAssets.periodEnd ? (
                  <span className="text-xs text-faint"> — year to {formatDateShort(fin.netAssets.periodEnd)}</span>
                ) : null}
              </h3>
              <dl className="kv-grid">
                <Fact label="Net assets" tip="Capital and reserves as filed - what the brief calls net assets (liabilities). Every figure here is labelled with the period it covers, since accounts can be up to 21 months old and still be the newest filed.">
                  {fin.netAssets.value !== null ? (
                    <>
                      {formatMoney(fin.netAssets.value)} <TrendBadge value={fin.netAssetsTrend} />
                    </>
                  ) : null}
                </Fact>
                <Fact
                  label="Turnover"
                  tip="UK small and micro companies are not required to file a profit and loss account, so this is genuinely absent for most companies here - not a gap in this application."
                >
                  {fin.turnover.notFiled ? (
                    <span className="value-absent">Not filed (exempt at this size)</span>
                  ) : fin.turnover.value !== null ? (
                    <>
                      {formatMoney(fin.turnover.value)} <TrendBadge value={fin.turnoverTrend} />
                    </>
                  ) : null}
                </Fact>
                <Fact label="Employees">{fin.employeeCount.value ?? null}</Fact>
                <Fact label={fin.retainedEarningsMovement.label} tip={fin.retainedEarningsMovement.caveat}>
                  {fin.retainedEarningsMovement.value !== null
                    ? formatMoney(fin.retainedEarningsMovement.value)
                    : null}
                </Fact>
              </dl>
            </section>

            <section className="profile-card">
              <h3 className="profile-section-title">Health flags</h3>
              <div className="pill-row">
                <FlagBadge value={flags.accountsOverdue} yesLabel="Accounts overdue" noLabel="Accounts up to date" unknownLabel="Accounts: not held" warnOnYes />
                <FlagBadge value={flags.confirmationOverdue} yesLabel="Confirmation statement overdue" noLabel="Confirmation statement up to date" unknownLabel="Confirmation: not held" warnOnYes />
                <FlagBadge value={flags.insolvencyHistory} yesLabel="Insolvency history" noLabel="No insolvency history" unknownLabel="Insolvency: not held" warnOnYes />
                <FlagBadge value={flags.outstandingCharges} yesLabel="Outstanding charges" noLabel="No outstanding charges" unknownLabel="Charges: not held" warnOnYes />
                <FlagBadge value={flags.dormant} yesLabel="Dormant" noLabel="Not dormant" unknownLabel="Dormant: not held" warnOnYes />
                <FlagBadge value={flags.recentlyIncorporated} yesLabel="Recently incorporated" noLabel="Established" unknownLabel="Age: not held" />
                <FlagBadge value={flags.accountsStale} yesLabel="Accounts over 21 months stale" noLabel="Accounts recent" unknownLabel="Freshness: not held" warnOnYes />
                <FlagBadge value={flags.proposalToStrikeOff} yesLabel="Proposal to strike off" noLabel="No strike-off proposal" unknownLabel="Strike-off: not held" warnOnYes />
              </div>
            </section>
          </div>

          <section className="profile-card">
            <h3 className="profile-section-title">
              Filed periods
              <span
                className="info-tip tip tip-below"
                data-tip="One row per filed period, newest first. A filed balance sheet always carries the prior year's comparative column, which is why two rows usually arrive from a single document."
              >
                i
              </span>
            </h3>
            {periods.length > 0 ? (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Year to</th>
                      <th scope="col">Filed</th>
                      <th scope="col">Net assets</th>
                      <th scope="col">Turnover</th>
                      <th scope="col">Employees</th>
                      <th scope="col">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map((period) => (
                      <tr key={period.periodEnd}>
                        <td>
                          {formatDateShort(period.periodEnd)}
                          {period.isComparative ? (
                            <span className="text-xs text-faint"> (comparative)</span>
                          ) : null}
                          {period.isRestated ? (
                            <span
                              className="badge badge-warn tip tip-below"
                              data-tip="A later filing's comparative column disagreed with what was originally filed for this period - surfaced rather than silently overwritten."
                            >
                              restated
                            </span>
                          ) : null}
                        </td>
                        <td>{period.filedOn ? formatDateShort(period.filedOn) : null}</td>
                        <td>
                          {period.netAssets !== null ? (
                            formatMoney(period.netAssets)
                          ) : (
                            <span className="value-absent">—</span>
                          )}
                        </td>
                        <td>
                          {period.turnover !== null ? (
                            formatMoney(period.turnover)
                          ) : (
                            <span className="value-absent">Not filed</span>
                          )}
                        </td>
                        <td>{period.employeeCount ?? <span className="value-absent">—</span>}</td>
                        <td>{period.accountsType ?? null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="value-absent text-sm">No period figures could be read from the filed accounts.</p>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/** psc | officer, as a reader-facing label. */
function sourceLabel(sourceType) {
  return sourceType === 'psc' ? 'Person with significant control' : 'Officer';
}

/** One director's name and role in the narrow select list on the left of the Directors tab. */
function DirectorListItem({ director, isSelected, onSelect }) {
  return (
    <button
      type="button"
      className={`director-select-item${isSelected ? ' is-selected' : ''}`}
      onClick={() => onSelect(director.id)}
    >
      <span className="director-select-title">
        <span className="director-select-name">{director.fullName}</span>
        {director.isPrimary ? <span className="badge badge-ok badge-sm">Primary</span> : null}
        {!director.isActive ? <span className="badge badge-warn badge-sm">Resigned</span> : null}
      </span>
      <span className="director-select-meta">{director.roleTitle}</span>
    </button>
  );
}

/** The full set of facts for whichever one director is selected in the list beside it. */
function DirectorDetail({ director, ambiguous, controllingEntity, onSelect, selecting }) {
  return (
    <div className={`director-detail${director.isPrimary ? ' is-primary' : ''}`}>
      <div className="director-card-header">
        <span className="director-name">
          {director.fullName}
          {director.isPrimary ? <span className="badge badge-ok">Primary</span> : null}
          {!director.isActive ? (
            <span
              className="badge badge-warn tip tip-below"
              data-tip="No longer active - resigned as an officer, or ceased as a person with significant control."
            >
              {director.resignedOn ? `Resigned ${formatDateShort(director.resignedOn)}` : 'Resigned'}
            </span>
          ) : null}
        </span>
        {ambiguous && !director.isPrimary ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={selecting}
            onClick={() => onSelect(director.id)}
          >
            {selecting ? <span className="spinner" /> : null}
            Make primary
          </button>
        ) : null}
      </div>

      <dl className="kv-grid">
        <Fact label="Role">{director.roleTitle}</Fact>
        <Fact label="Source">{sourceLabel(director.sourceType)}</Fact>
        {director.pscKind ? (
          <Fact label="PSC kind" tip={director.pscKind.tip}>
            {director.pscKind.label}
          </Fact>
        ) : null}
        <Fact
          label="Ownership"
          tip={
            controllingEntity
              ? `Companies House only ever publishes a banded ownership figure, never an exact percentage. This director is from the officers list, not a PSC record, so they carry no ownership band of their own - that ${controllingEntity.ownershipBand ?? 'band'} sits with ${controllingEntity.name} above, not with any person.`
              : 'Companies House only ever publishes a banded ownership figure, never an exact percentage - this is the band as filed. Not held means this director carries no PSC ownership band on record (most often an officer with no direct shareholding).'
          }
        >
          {director.ownershipBand}
        </Fact>
        {director.votingRightsBand ? (
          <Fact label="Voting rights">{director.votingRightsBand}</Fact>
        ) : null}
        <Fact
          label="Appointed"
          tip={
            director.appointedOnApprox
              ? 'Companies House holds no exact appointment date for this officer - typically the case for a company incorporated before electronic filing began. This is its own approximation, not a confirmed date.'
              : undefined
          }
        >
          {director.appointedOn ? (
            <>
              {director.appointedOnApprox ? 'On or before ' : ''}
              {formatDateShort(director.appointedOn)}
            </>
          ) : null}
        </Fact>
        <Fact label="Address">
          {director.addressSameAsCompany ? (
            <span className="text-xs text-faint">Same as registered office</span>
          ) : director.address.length > 0 ? (
            <span className="profile-address">
              {director.address.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </span>
          ) : null}
        </Fact>
      </dl>
    </div>
  );
}

/**
 * An .alert that starts collapsed to a single-line pill, since the Directors
 * tab can show two of these at once and a reader who already knows the gist
 * ("controlled by a parent company", "pick a primary") shouldn't have to
 * scroll past two full paragraphs to reach the director list every time.
 */
function CollapsibleAlert({ tone, icon, pillLabel, title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="alert-collapsible">
      <button
        type="button"
        className={`pill alert-pill alert-pill-${tone}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {open ? <IconChevronDown /> : <IconChevronRight />}
        {icon}
        {pillLabel}
      </button>
      {open ? (
        <div className={`alert alert-${tone}`}>
          <span className="alert-icon">{icon}</span>
          <span className="alert-body">
            <span className="alert-title">{title}</span>
            {children}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function Directors({ company, onSelect, selecting }) {
  const dv = company.directors;
  const [selectedId, setSelectedId] = useState(null);
  const selectedDirector =
    dv.directors.find((director) => director.id === selectedId) ??
    dv.directors.find((director) => director.isPrimary) ??
    dv.directors[0] ??
    null;

  return (
    <div className="profile-tab">
      {!dv.checked ? (
        <div className="empty">
          <span className="empty-icon">
            <IconInfo />
          </span>
          <h3 className="empty-title">Directors not resolved yet</h3>
          <p className="empty-description">
            Press <strong>Resolve directors</strong> above, or acquire this company&rsquo;s SIC code at
            Full depth.
          </p>
        </div>
      ) : dv.directors.length === 0 ? (
        <div className="empty">
          <span className="empty-icon">
            <IconInfo />
          </span>
          <h3 className="empty-title">No director could be resolved</h3>
          <p className="empty-description">
            Companies House holds no person with significant control and no active officer for this
            company — genuinely rare, but it happens for some LLPs and companies still mid-incorporation.
            That is a different answer from &ldquo;not checked yet&rdquo;.
          </p>
        </div>
      ) : (
        <>
          {dv.controllingEntity || dv.ambiguous ? (
            <div className="profile-alert-stack">
              {dv.controllingEntity ? (
                <CollapsibleAlert
                  tone="info"
                  icon={<IconInfo />}
                  pillLabel="Controlled by another company"
                  title="Controlled by another company, not a named person"
                >
                  Companies House's significant control record for this company names{' '}
                  <strong>{dv.controllingEntity.name}</strong>
                  {dv.controllingEntity.registrationNumber ? (
                    <>
                      {' '}
                      (
                      <a
                        className="link-external"
                        href={`https://find-and-update.company-information.service.gov.uk/company/${dv.controllingEntity.registrationNumber}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {dv.controllingEntity.registrationNumber}
                        <IconExternal />
                      </a>
                      )
                    </>
                  ) : null}
                  {dv.controllingEntity.ownershipBand ? (
                    <> — {dv.controllingEntity.ownershipBand} ownership</>
                  ) : null}
                  . The directors below are pulled from the officers list instead, which is why none of them
                  show an ownership percentage - that band belongs to {dv.controllingEntity.name}, not to any
                  person named here.
                </CollapsibleAlert>
              ) : null}

              {dv.ambiguous ? (
                <CollapsibleAlert
                  tone="warn"
                  icon={<IconAlert />}
                  pillLabel="Ambiguous primary director"
                  title="More than one equally plausible primary director"
                >
                  Resolution found a tie rather than guessing. Choose one below as the primary.
                </CollapsibleAlert>
              ) : null}
            </div>
          ) : null}

          <section className="profile-card">
            <h3 className="profile-section-title">
              Directors
              <span
                className="info-tip tip tip-below"
                data-tip="Persons with significant control first, ranked by ownership band. Falls through to the officers list only when control sits with another company or LLP rather than a named person. Resigned officers are always excluded."
              >
                i
              </span>
            </h3>
            <div className="director-layout">
              <div className="director-select-list">
                {dv.directors.map((director) => (
                  <DirectorListItem
                    key={director.id}
                    director={director}
                    isSelected={director.id === selectedDirector?.id}
                    onSelect={setSelectedId}
                  />
                ))}
              </div>
              {selectedDirector ? (
                <DirectorDetail
                  director={selectedDirector}
                  ambiguous={dv.ambiguous}
                  controllingEntity={dv.controllingEntity}
                  onSelect={onSelect}
                  selecting={selecting}
                />
              ) : null}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function History({ company }) {
  if (company.history.length === 0) {
    return (
      <div className="empty">
        <h3 className="empty-title">No acquisition run recorded finding this company</h3>
        <p className="empty-description">
          It was almost certainly added by the direct lookup box, which fetches one company straight
          from Companies House and belongs to no run. Acquire one of its SIC codes from the
          catalogue to bring in the rest of its market.
        </p>
        <div className="empty-actions">
          <Link className="btn btn-primary" to="/catalogue">
            Go to the catalogue
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-tab">
      <section className="profile-card">
        <h3 className="profile-section-title">
          Acquisition history
          <span
            className="info-tip tip tip-below"
            data-tip="Read from the run log, which is immutable — it records what a run did at the time. A company can therefore show a history entry naming a SIC code it no longer files."
          >
            i
          </span>
        </h3>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Found</th>
                <th scope="col">Under</th>
                <th scope="col">Outcome</th>
                <th scope="col">Run</th>
              </tr>
            </thead>
            <tbody>
              {company.history.map((entry, index) => {
                const meta = OUTCOME_META[entry.outcome] ?? { label: entry.outcome, tone: '' };
                return (
                  <tr key={`${entry.uuid}-${index}`}>
                    <td className="text-sm">{new Date(entry.foundAt).toLocaleString()}</td>
                    <td>
                      <span className="mono">{entry.sicCode}</span>
                      <span className="text-xs text-faint"> {entry.sicDescription ?? ''}</span>
                    </td>
                    <td>
                      <span className={`badge ${meta.tone}`}>{meta.label}</span>
                      {entry.detail ? (
                        <span className="text-xs text-faint"> {entry.detail}</span>
                      ) : null}
                    </td>
                    <td>
                      <Link className="btn btn-ghost btn-sm" to={`/log/${entry.uuid}`}>
                        <IconLog />
                        Open run
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/** Who receives mail for a domain, in plain English - keyed by EmailDomainDto.mxProvider. */
const MX_PROVIDER_LABELS = {
  google: 'Google Workspace',
  microsoft: 'Microsoft 365',
  proofpoint: 'Proofpoint (hosted mail filtering)',
  mimecast: 'Mimecast (hosted mail filtering)',
  barracuda: 'Barracuda (hosted mail filtering)',
  other: 'Self-hosted or another provider',
  none: 'No mail server found',
};

/** How a domain's naming convention became known - keyed by EmailDomainDto.patternSource. */
const PATTERN_SOURCE_LABELS = {
  manual: 'confirmed by hand',
  'smtp-verified': 'confirmed by SMTP probe',
};

/** Mirrors the server's describePattern() labels for the one place a raw pattern key reaches the client unlabelled: EmailDomainDto.confirmedPattern. */
const PATTERN_LABELS = {
  'first.last': 'first.last (john.smith@)',
  first: 'first (john@)',
  flast: 'first initial + last (jsmith@)',
  firstlast: 'firstlast (johnsmith@)',
  'f.last': 'first initial.last (j.smith@)',
  first_last: 'first_last (john_smith@)',
  firstl: 'first + last initial (johns@)',
  last: 'last (smith@)',
  'last.first': 'last.first (smith.john@)',
  fl: 'initials (js@)',
  role: 'company mailbox',
};

/**
 * The honesty layer Prompt 10 requires: every verify status explains itself,
 * and `catch_all`/`inconclusive`/`blocked` are visibly distinct from
 * `invalid` - none of the three is evidence against the address, only
 * `invalid` is.
 */
const VERIFY_STATUS_META = {
  unknown: {
    label: 'Not probed',
    tone: 'badge-unknown',
    tip: 'No SMTP probe has been attempted for this address yet - neither evidence for it nor against it.',
  },
  valid: {
    label: 'Confirmed',
    tone: 'badge-ok',
    tip: 'The receiving mail server accepted this address at the SMTP stage (EHLO, MAIL FROM, RCPT TO). No message was ever sent - only that one step was checked.',
  },
  invalid: {
    label: 'Rejected',
    tone: 'badge-error',
    tip: 'The receiving mail server explicitly rejected this address with a 550-series response. A genuine negative result, not a guess.',
  },
  catch_all: {
    label: 'Domain accepts everything',
    tone: 'badge-warn',
    tip: 'This domain’s mail server accepts every address at the SMTP stage, so a probe here can never confirm one specific mailbox is real. Not evidence the address is wrong - the domain simply cannot be probed this way.',
  },
  inconclusive: {
    label: 'Unverifiable',
    tone: 'badge-unknown',
    tip: 'The mail server gave an ambiguous response (for example, greylisting), so the probe could not settle on an answer. Not evidence against this address.',
  },
  blocked: {
    label: 'Unverifiable',
    tone: 'badge-unknown',
    tip: 'A probe could not run at all for this address - see the domain notes above for the specific reason (no MX, a provider that accepts everything, SMTP probing switched off, or this network’s outbound port 25 being blocked). Never evidence against the address.',
  },
};

function EmailStatusBadge({ status }) {
  const meta = VERIFY_STATUS_META[status] ?? VERIFY_STATUS_META.unknown;
  return (
    <span className={`badge ${meta.tone} tip tip-below`} data-tip={meta.tip}>
      {meta.label}
    </span>
  );
}

/**
 * One candidate address, as a card rather than a table row - phase 10's
 * redesign. An address carries four attributes worth seeing at once
 * (pattern, likelihood, verify status, chosen state), which read as a wall
 * of small print in a table row; a card keeps the address itself the most
 * prominent thing on it, with room for a one-tap copy and the same
 * tooltip-carrying status badge used everywhere else in the app.
 */
function EmailCandidateCard({ guess, onChoose, choosing }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(guess.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard permission denied or unavailable - the address is still
      // selectable by hand, so this is not worth surfacing as an error.
    }
  };

  return (
    <div className={`email-card${guess.isChosen ? ' is-chosen' : ''}`}>
      <div className="email-card-header">
        <span className="email-card-address mono">{guess.email}</span>
        <button
          type="button"
          className="btn btn-ghost btn-icon tip tip-below tip-left"
          data-tip={copied ? 'Copied' : 'Copy address'}
          onClick={handleCopy}
        >
          {copied ? <IconCheck /> : <IconCopy />}
        </button>
      </div>

      <div className="email-card-body">
        <div className="email-card-row">
          <span className="email-card-label">Pattern</span>
          <span className="email-card-value">{guess.patternLabel}</span>
        </div>
        <div className="email-card-row">
          <span className="email-card-label">Likelihood</span>
          <span className="email-card-likelihood">
            <span className="progress progress-mini">
              <span className="progress-bar" style={{ width: `${guess.priorScore}%` }} />
            </span>
            <span className="email-card-value">{guess.priorScore}%</span>
          </span>
        </div>
      </div>

      <div className="email-card-footer">
        <EmailStatusBadge status={guess.verifyStatus} />
        {guess.isChosen ? (
          <span className="badge badge-ok badge-sm">
            <IconCheck />
            Chosen
          </span>
        ) : (
          <button
            type="button"
            className="btn btn-sm"
            disabled={choosing}
            onClick={() => onChoose(guess.id)}
          >
            {choosing ? <span className="spinner" /> : null}
            Choose
          </button>
        )}
      </div>
    </div>
  );
}

/** A director's (or the company's) full ranked candidate set, as a card grid. */
function EmailCandidateGrid({ guesses, onChoose, choosing }) {
  return (
    <div className="email-card-grid">
      {guesses.map((guess) => (
        <EmailCandidateCard key={guess.id} guess={guess} onChoose={onChoose} choosing={choosing} />
      ))}
    </div>
  );
}

/** Splits the flat directorGuesses list back into per-director groups, preserving server order. */
function groupGuessesByDirector(guesses) {
  const order = [];
  const map = new Map();
  for (const guess of guesses) {
    if (!map.has(guess.directorId)) {
      map.set(guess.directorId, { directorId: guess.directorId, directorName: guess.directorName, guesses: [] });
      order.push(guess.directorId);
    }
    map.get(guess.directorId).guesses.push(guess);
  }
  return order.map((id) => map.get(id));
}

function Contact({
  emails,
  emailsError,
  loadingEmails,
  onChoose,
  choosing,
  egress,
  onGoToOverview,
}) {
  if (!emails) {
    if (loadingEmails) {
      return (
        <div className="loading-panel">
          <span className="spinner" />
          Loading contact data
        </div>
      );
    }
    // The fetch effect has not landed yet (or failed before setting anything).
    return emailsError ? (
      <div className="alert alert-error">
        <span className="alert-icon">
          <IconAlert />
        </span>
        <span className="alert-body">{emailsError}</span>
      </div>
    ) : null;
  }

  const domain = emails.domain;
  const noWebsite = !domain && emails.blockedReason?.includes('website');

  return (
    <div className="profile-tab">
      {emailsError ? (
        <div className="alert alert-error">
          <span className="alert-icon">
            <IconAlert />
          </span>
          <span className="alert-body">{emailsError}</span>
        </div>
      ) : null}

      {!domain ? (
        <div className="empty">
          <span className="empty-icon">
            <IconInfo />
          </span>
          <h3 className="empty-title">
            {noWebsite ? 'No website on file yet' : 'No candidate addresses generated yet'}
          </h3>
          <p className="empty-description">
            {emails.blockedReason}
            {noWebsite ? null : (
              <>
                {' '}
                Press <strong>Generate addresses</strong> above.
              </>
            )}
          </p>
          {noWebsite ? (
            <div className="empty-actions">
              <button type="button" className="btn btn-primary" onClick={onGoToOverview}>
                Go to Overview to discover a website
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {egress && !egress.available ? (
            <div className="profile-alert-stack">
              <CollapsibleAlert
                tone="info"
                icon={<IconInfo />}
                pillLabel="SMTP probing unavailable"
                title="SMTP probing cannot confirm anything from this machine right now"
              >
                {egress.detail}
              </CollapsibleAlert>
            </div>
          ) : null}

          <section className="profile-card">
            <h3 className="profile-section-title">
              Domain
              <span
                className="info-tip tip tip-below"
                data-tip="Resolved once per domain and reused for every director there - the whole point of email guessing being domain-first rather than address-first."
              >
                i
              </span>
            </h3>
            <dl className="kv-grid">
              <Fact label="Domain">
                <span className="mono">{domain.domain}</span>
              </Fact>
              <Fact label="Mail hosted by" tip="Who receives mail for this domain, read from its MX record.">
                {domain.mxHost ? (
                  MX_PROVIDER_LABELS[domain.mxProvider] ?? domain.mxProvider
                ) : (
                  <span className="value-absent">No MX record - this domain cannot receive mail</span>
                )}
              </Fact>
              <Fact
                label="Probing"
                tip="Whether an SMTP probe against this domain could prove anything at all. 'Not possible' is not this application failing - some domains genuinely cannot be probed this way."
              >
                {domain.probeSupported ? (
                  <span className="badge badge-ok">Supported</span>
                ) : (
                  <>
                    <span className="badge badge-unknown">Not possible</span>
                    {domain.probeSkipReason ? (
                      <p className="value-absent text-sm">{domain.probeSkipReason}</p>
                    ) : null}
                  </>
                )}
              </Fact>
              <Fact
                label="Catch-all"
                tip="A catch-all domain accepts every address at the SMTP stage, so no probe result there can confirm one specific mailbox."
              >
                {domain.isCatchAll === null ? (
                  <span className="value-absent">Not tested yet</span>
                ) : (
                  <FlagBadge
                    value={domain.isCatchAll}
                    yesLabel="Accepts everything"
                    noLabel="Does not accept everything"
                    unknownLabel="Not tested"
                    warnOnYes
                  />
                )}
              </Fact>
              {domain.confirmedPattern ? (
                <Fact
                  label="Known naming pattern"
                  tip="Learned from a human choosing an address, or an SMTP probe confirming one - never guessed into existence. Every other director at this domain is generated at high confidence once this is known."
                >
                  {PATTERN_LABELS[domain.confirmedPattern] ?? domain.confirmedPattern}
                  {domain.patternSource ? (
                    <span className="text-xs text-faint">
                      {' '}
                      &mdash; {PATTERN_SOURCE_LABELS[domain.patternSource] ?? domain.patternSource}
                    </span>
                  ) : null}
                </Fact>
              ) : null}
            </dl>
          </section>

          {emails.blockedReason ? <p className="value-absent text-sm">{emails.blockedReason}</p> : null}

          {emails.directorGuesses.length > 0 ? (
            <section className="profile-card">
              <h3 className="profile-section-title">Directors</h3>
              <ul className="director-list">
                {groupGuessesByDirector(emails.directorGuesses).map((group) => (
                  <li key={group.directorId} className="director-card">
                    <div className="director-card-header">
                      <span className="director-name">{group.directorName}</span>
                      <span className="badge badge-sm">
                        {group.guesses.length} address{group.guesses.length === 1 ? '' : 'es'}
                      </span>
                    </div>
                    <EmailCandidateGrid guesses={group.guesses} onChoose={onChoose} choosing={choosing} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {emails.roleGuesses.length > 0 ? (
            <section className="profile-card">
              <h3 className="profile-section-title">
                Company mailboxes
                <span
                  className="info-tip tip tip-below"
                  data-tip="Conventional addresses that belong to the company rather than any named person - info@, enquiries@ and similar. More likely to be monitored by a gatekeeper than a decision maker."
                >
                  i
                </span>
              </h3>
              <EmailCandidateGrid guesses={emails.roleGuesses} onChoose={onChoose} choosing={choosing} />
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

export default function CompanyProfile({ companyNumber, onLoaded, onDeleted }) {
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [refreshingFinancials, setRefreshingFinancials] = useState(false);
  const [refreshingDirectors, setRefreshingDirectors] = useState(false);
  const [selectingDirector, setSelectingDirector] = useState(false);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [discoveringWebsite, setDiscoveringWebsite] = useState(false);
  const [settingWebsite, setSettingWebsite] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Phase 10. Unlike website/directors/financials, contact data is not part
  // of the profile fetch - it is fetched lazily the first time the Contact
  // tab opens, so opening a company never spends a DNS lookup and a
  // guess-table read it might not need.
  const [emails, setEmails] = useState(null);
  const [emailsError, setEmailsError] = useState(null);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [generatingEmails, setGeneratingEmails] = useState(false);
  const [probingEmails, setProbingEmails] = useState(false);
  const [choosingEmail, setChoosingEmail] = useState(false);
  const [egress, setEgress] = useState(null);

  const handleRefresh = async () => {
    if (!companyNumber) return;
    setRefreshing(true);
    setActionError(null);
    try {
      const result = await companiesApi.refresh(companyNumber);
      setCompany(result.company);
    } catch (caught) {
      setActionError(caught.message ?? 'Could not refresh this company from Companies House.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async () => {
    if (!companyNumber) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await companiesApi.deleteCompany(companyNumber);
      setShowDeleteConfirm(false);
      onDeleted?.(companyNumber);
    } catch (caught) {
      setDeleteError(caught.message ?? 'Could not delete this company.');
    } finally {
      setDeleting(false);
    }
  };

  const handleRefreshFinancials = async () => {
    if (!companyNumber) return;
    setRefreshingFinancials(true);
    setActionError(null);
    try {
      const result = await companiesApi.refreshFinancials(companyNumber);
      setCompany(result.company);
    } catch (caught) {
      setActionError(caught.message ?? "Could not fetch this company's accounts from Companies House.");
    } finally {
      setRefreshingFinancials(false);
    }
  };

  const handleRefreshDirectors = async () => {
    if (!companyNumber) return;
    setRefreshingDirectors(true);
    setActionError(null);
    try {
      const result = await companiesApi.refreshDirectors(companyNumber);
      setCompany(result.company);
    } catch (caught) {
      setActionError(caught.message ?? "Could not resolve this company's directors from Companies House.");
    } finally {
      setRefreshingDirectors(false);
    }
  };

  const handleSelectDirector = async (directorId) => {
    if (!companyNumber) return;
    setSelectingDirector(true);
    setActionError(null);
    try {
      const result = await companiesApi.selectDirector(companyNumber, directorId);
      setCompany(result.company);
    } catch (caught) {
      setActionError(caught.message ?? 'Could not choose that director.');
    } finally {
      setSelectingDirector(false);
    }
  };

  const handleDiscoverWebsite = async () => {
    if (!companyNumber) return;
    setDiscoveringWebsite(true);
    setActionError(null);
    try {
      const result = await companiesApi.discoverWebsite(companyNumber);
      setCompany(result.company);
    } catch (caught) {
      setActionError(caught.message ?? "Could not run website discovery for this company.");
    } finally {
      setDiscoveringWebsite(false);
    }
  };

  const handleSetWebsite = async (website) => {
    if (!companyNumber) return;
    setSettingWebsite(true);
    setActionError(null);
    try {
      const result = await companiesApi.setWebsite(companyNumber, website);
      setCompany(result.company);
    } catch (caught) {
      setActionError(caught.message ?? 'Could not save that website.');
    } finally {
      setSettingWebsite(false);
    }
  };

  const handleSetOverride = async (isAgency) => {
    if (!companyNumber) return;
    setOverrideSaving(true);
    setActionError(null);
    try {
      const result = await companiesApi.setAgencyOverride(companyNumber, isAgency);
      setCompany(result.company);
    } catch (caught) {
      setActionError(caught.message ?? 'Could not save that classification.');
    } finally {
      setOverrideSaving(false);
    }
  };

  const handleGenerateEmails = async () => {
    if (!companyNumber) return;
    setGeneratingEmails(true);
    setEmailsError(null);
    try {
      const result = await companiesApi.generateEmails(companyNumber, { refresh: Boolean(emails?.domain) });
      setEmails(result.emails);
    } catch (caught) {
      setEmailsError(caught.message ?? 'Could not generate candidate addresses for this company.');
    } finally {
      setGeneratingEmails(false);
    }
  };

  const handleProbeEmails = async () => {
    if (!companyNumber) return;
    setProbingEmails(true);
    setEmailsError(null);
    try {
      const result = await companiesApi.probeEmails(companyNumber);
      setEmails(result.emails);
    } catch (caught) {
      setEmailsError(caught.message ?? 'Could not probe candidate addresses for this company.');
    } finally {
      setProbingEmails(false);
    }
  };

  const handleChooseEmail = async (guessId) => {
    if (!companyNumber) return;
    setChoosingEmail(true);
    setEmailsError(null);
    try {
      const result = await companiesApi.chooseEmail(companyNumber, guessId);
      setEmails(result.emails);
    } catch (caught) {
      setEmailsError(caught.message ?? 'Could not choose that address.');
    } finally {
      setChoosingEmail(false);
    }
  };

  useEffect(() => {
    if (!companyNumber) return undefined;

    const controller = new AbortController();
    setCompany(null);
    setError(null);
    setActionError(null);
    // Back to Overview on every company: a tab chosen for one company means
    // nothing for the next, and landing on an empty History reads as a bug.
    setTab('overview');
    // A new company's contact data has to be re-fetched, not carried over
    // from whichever company was open before.
    setEmails(null);
    setEmailsError(null);

    companiesApi
      .get(companyNumber, { signal: controller.signal })
      .then((result) => {
        setCompany(result.company);
        onLoaded?.(result.company);
      })
      .catch((caught) => {
        // Aborted because the operator moved on to another company - not a
        // failure, and showing one would flash an error on every arrow keypress.
        if (caught.name === 'AbortError') return;
        setError(caught.message ?? 'Could not load this company.');
      });

    return () => controller.abort();
    // `onLoaded` is a render-time closure from the page; depending on it would
    // refetch the company on every keystroke in the search box.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyNumber]);

  // Fetches contact data the first time the Contact tab is opened for this
  // company, not before - opening a company should not spend a DNS lookup
  // and a guess-table read it might never need. `egress` describes this
  // machine's own network, not any one company, so it is fetched once and
  // reused across every company for the rest of the session.
  useEffect(() => {
    if (tab !== 'contact' || !companyNumber || emails !== null) return undefined;

    const controller = new AbortController();
    setLoadingEmails(true);
    setEmailsError(null);

    Promise.all([
      companiesApi.getEmails(companyNumber, { signal: controller.signal }),
      egress === null ? companiesApi.smtpEgress({ signal: controller.signal }) : Promise.resolve(egress),
    ])
      .then(([emailsResult, egressResult]) => {
        setEmails(emailsResult.emails);
        setEgress(egressResult);
      })
      .catch((caught) => {
        if (caught.name === 'AbortError') return;
        setEmailsError(caught.message ?? 'Could not load contact data for this company.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingEmails(false);
      });

    return () => controller.abort();
    // Deliberately excludes `egress` and `loadingEmails` from the dependency
    // array. `egress` is read once per mount to decide whether to re-fetch
    // it, not tracked as a trigger - depending on it would refetch every
    // company's emails the moment egress first resolves. `loadingEmails` is
    // set to true by this very effect body - including it here made React
    // treat that state change as a fresh trigger, clean up (abort) this same
    // in-flight fetch the instant it started, and then silently discard the
    // correct response that still arrived afterward. Caught live: the
    // Contact tab hung on "Loading contact data" forever despite both
    // requests completing with 200 responses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, companyNumber, emails]);

  if (!companyNumber) {
    return (
      <div className="detail">
        <div className="detail-body">
          <div className="empty">
            <h3 className="empty-title">Select a company to see its profile</h3>
            <p className="empty-description">
              Choose one from the list on the left, or use <strong>Look up a company</strong> above
              it to fetch one straight from Companies House by name or company number.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="detail">
        <div className="detail-body">
          <div className="alert alert-error">
            <span className="alert-icon">
              <IconAlert />
            </span>
            <span className="alert-body">
              <span className="alert-title">Could not open this company</span>
              {error}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="detail">
        <div className="detail-body">
          <div className="loading-panel">
            <span className="spinner" />
            Loading the company
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="detail">
      <div className="detail-header">
        <div className="row row-between">
          <div className="profile-heading">
            <h2 className="profile-name">{company.name}</h2>
            <a
              className="link-external mono text-sm"
              href={`https://find-and-update.company-information.service.gov.uk/company/${company.companyNumber}`}
              target="_blank"
              rel="noreferrer"
            >
              {company.companyNumber}
              <IconExternal />
            </a>
            <span className={`badge ${company.status === 'active' ? 'badge-ok' : 'badge-unknown'}`}>
              <span className="badge-dot" />
              {company.statusLabel ?? 'Unknown status'}
            </span>
            {company.typeLabel ? (
              <span className="text-xs text-faint">{company.typeLabel}</span>
            ) : null}
            {company.incorporatedOn ? (
              <span className="text-xs text-faint">
                incorporated {formatDate(company.incorporatedOn)}
              </span>
            ) : null}
          </div>
          <div className="row" style={{ flexShrink: 0 }}>
            <FavoriteStar companyNumber={company.companyNumber} size="lg" />
            <AddToListMenu companyNumber={company.companyNumber} />
            <span
              className="tip tip-below tip-left"
              data-tip={`Held since ${new Date(company.firstSeenAt).toLocaleString()}, last updated ${new Date(company.updatedAt).toLocaleString()}. Company data from Companies House.`}
              style={{ display: 'flex', alignItems: 'center', color: 'var(--ink-faint)', cursor: 'default' }}
            >
              <IconInfo />
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm tip tip-below tip-left"
              onClick={() => {
                setDeleteError(null);
                setShowDeleteConfirm(true);
              }}
              data-tip="Permanently removes this company and everything derived about it from the registry."
            >
              <IconTrash />
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="tabs-row">
        <nav className="tabs" aria-label="Company profile sections">
          {TABS.map((entry) => {
            const emailCount = emails ? emails.directorGuesses.length + emails.roleGuesses.length : 0;
            return (
              <button
                key={entry.id}
                type="button"
                className={`tab${tab === entry.id ? ' is-active' : ''}`}
                onClick={() => setTab(entry.id)}
              >
                {entry.label}
                {entry.id === 'history' && company.history.length > 0 ? (
                  <span className="tab-badge">{company.history.length}</span>
                ) : null}
                {entry.id === 'directors' && company.directors.ambiguous ? (
                  <span className="badge badge-warn badge-sm">Review</span>
                ) : entry.id === 'directors' && company.directors.directors.length > 0 ? (
                  <span className="tab-badge">{company.directors.directors.length}</span>
                ) : null}
                {entry.id === 'contact' && emailCount > 0 ? (
                  <span className="tab-badge">{emailCount}</span>
                ) : null}
              </button>
            );
          })}
        </nav>

        {/* The one sync action for whichever tab is open - each tab does a
            different kind of fetch, but the button always lives here so it
            never moves as the operator switches tabs. */}
        <div className="tab-actions">
          {tab === 'overview' ? (
            <button
              type="button"
              className="btn btn-sm tip tip-below tip-left"
              onClick={handleRefresh}
              disabled={refreshing}
              data-tip="Fetches this company's full profile from Companies House now: previous names, accounts and confirmation-statement dates, insolvency and charges flags, and re-classifies it. Spends one API call."
            >
              {refreshing ? <span className="spinner" /> : <IconRefresh />}
              Refresh from Companies House
            </button>
          ) : null}
          {tab === 'financials' ? (
            <button
              type="button"
              className="btn btn-sm tip tip-below tip-left"
              onClick={handleRefreshFinancials}
              disabled={refreshingFinancials}
              data-tip="Fetches this company's filed accounts from Companies House now: filing history, the latest accounts document, charges and insolvency history. Spends several API calls."
            >
              {refreshingFinancials ? <span className="spinner" /> : <IconRefresh />}
              Fetch accounts
            </button>
          ) : null}
          {tab === 'directors' ? (
            <button
              type="button"
              className="btn btn-sm tip tip-below tip-left"
              onClick={handleRefreshDirectors}
              disabled={refreshingDirectors}
              data-tip="Resolves this company's directors from Companies House now: persons with significant control first, the officers list as a fallback when control sits with another company. Spends one or two API calls."
            >
              {refreshingDirectors ? <span className="spinner" /> : <IconRefresh />}
              Resolve directors
            </button>
          ) : null}
          {tab === 'contact' && emails?.domain ? (
            <>
              <button
                type="button"
                className="btn btn-ghost btn-sm tip tip-below tip-left"
                onClick={handleGenerateEmails}
                disabled={generatingEmails}
                data-tip="Regenerates every unconfirmed candidate from this company's directors and the standard role mailboxes. A chosen address is never touched."
              >
                {generatingEmails ? <span className="spinner" /> : <IconRefresh />}
                Regenerate
              </button>
              <button
                type="button"
                className="btn btn-sm tip tip-below tip-left"
                onClick={handleProbeEmails}
                disabled={probingEmails}
                data-tip="Soft-probes the top candidates by SMTP: opens a conversation with the mail server and asks whether it would accept the address, without ever sending a message. Only works where the domain, its host and this network all allow it - see the notes below."
              >
                {probingEmails ? <span className="spinner" /> : null}
                Probe addresses
              </button>
            </>
          ) : null}
          {tab === 'contact' && emails && !emails.domain && !emails.blockedReason?.includes('website') ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleGenerateEmails}
              disabled={generatingEmails}
            >
              {generatingEmails ? <span className="spinner" /> : null}
              Generate addresses
            </button>
          ) : null}
        </div>
      </div>

      <div className="detail-body">
        {actionError ? (
          <div className="alert alert-error">
            <span className="alert-icon">
              <IconAlert />
            </span>
            <span className="alert-body">{actionError}</span>
          </div>
        ) : null}

        {tab === 'overview' ? (
          <Overview
            company={company}
            onSetOverride={handleSetOverride}
            overrideSaving={overrideSaving}
            onDiscoverWebsite={handleDiscoverWebsite}
            discoveringWebsite={discoveringWebsite}
            onSetWebsite={handleSetWebsite}
            settingWebsite={settingWebsite}
          />
        ) : null}
        {tab === 'financials' ? <Financials company={company} /> : null}
        {tab === 'directors' ? (
          <Directors company={company} onSelect={handleSelectDirector} selecting={selectingDirector} />
        ) : null}
        {tab === 'contact' ? (
          <Contact
            companyNumber={companyNumber}
            emails={emails}
            emailsError={emailsError}
            loadingEmails={loadingEmails}
            onChoose={handleChooseEmail}
            choosing={choosingEmail}
            egress={egress}
            onGoToOverview={() => setTab('overview')}
          />
        ) : null}
        {tab === 'history' ? <History company={company} /> : null}

      </div>

      {showDeleteConfirm ? (
        <DeleteCompanyDialog
          company={company}
          busy={deleting}
          error={deleteError}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={() => void handleDelete()}
        />
      ) : null}
    </div>
  );
}
